import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShoppingCartSimple,
  Minus,
  Plus,
  FileArrowDown,
  Warning,
  CaretLeft,
  ShieldCheck,
} from "@phosphor-icons/react";
import api, { API, formatCurrency, DEFAULT_PRODUCT_IMAGE } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

function DataRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex flex-col gap-1 border-b border-cyan/10 py-3 sm:flex-row sm:items-start sm:justify-between">
      <span className="font-mono text-[11px] uppercase tracking-widest text-teal">{label}</span>
      <span className="max-w-xs break-words font-mono text-sm text-white sm:text-right">{value}</span>
    </div>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api
      .get(`/products/${id}`)
      .then(({ data }) => setProduct(data))
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound)
    return (
      <div className="mx-auto max-w-2xl px-6 py-32 text-center" data-testid="product-not-found">
        <h1 className="font-heading text-3xl font-bold text-white">Compound not found</h1>
        <Link to="/shop" className="mt-6 inline-block font-mono text-sm uppercase tracking-wider text-cyan hover:text-cyan-bright">
          ← Back to catalog
        </Link>
      </div>
    );

  if (!product)
    return (
      <div className="mx-auto max-w-7xl px-6 py-20">
        <div className="grid gap-12 lg:grid-cols-[7fr_5fr]">
          <div className="aspect-square animate-pulse border border-cyan/10 bg-navy-surface" />
          <div className="space-y-4">
            <div className="h-10 w-2/3 animate-pulse bg-navy-surface" />
            <div className="h-24 animate-pulse bg-navy-surface" />
          </div>
        </div>
      </div>
    );

  const addToCart = () => {
    addItem(product, qty);
    toast.success(`${qty} × ${product.name} added to cart`);
  };

  return (
    <div data-testid="product-detail-page" className="mx-auto max-w-7xl px-6 py-12 sm:px-8 lg:px-12">
      <button
        onClick={() => navigate(-1)}
        data-testid="back-button"
        className="mb-8 flex items-center gap-1.5 font-mono text-xs uppercase tracking-wider text-teal-muted transition-colors hover:text-cyan"
      >
        <CaretLeft size={14} /> Back
      </button>

      <div className="grid gap-10 lg:grid-cols-[7fr_5fr]">
        {/* Image */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="relative border border-cyan/20 bg-navy-deep"
        >
          <div className="grid-texture absolute inset-0 opacity-40" />
          <img
            src={product.image_url || DEFAULT_PRODUCT_IMAGE}
            alt={product.name}
            className="relative aspect-square w-full object-cover"
            onError={(e) => {
              if (e.currentTarget.src !== DEFAULT_PRODUCT_IMAGE) e.currentTarget.src = DEFAULT_PRODUCT_IMAGE;
            }}
          />
          {product.purity && (
            <span className="absolute left-4 top-4 border border-cyan/30 bg-navy/80 px-3 py-1.5 font-mono text-xs uppercase tracking-wider text-cyan backdrop-blur">
              Purity {product.purity}
            </span>
          )}
        </motion.div>

        {/* Data */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <Link
            to={`/shop?category=${encodeURIComponent(product.category)}`}
            className="font-mono text-xs uppercase tracking-[0.2em] text-cyan hover:text-cyan-bright"
          >
            {product.category}
          </Link>
          <h1 className="mt-2 font-heading text-3xl font-black tracking-tighter text-white sm:text-4xl">
            {product.name}
          </h1>
          <p className="mt-4 text-base leading-relaxed text-teal-muted">{product.description}</p>

          {product.purity && (
            <div className="mt-6 flex items-baseline gap-3 border border-cyan/15 bg-navy-surface px-5 py-4">
              <span className="font-mono text-xs uppercase tracking-widest text-teal">Verified Purity</span>
              <span className="font-mono text-4xl font-bold text-cyan text-glow">{product.purity}</span>
            </div>
          )}

          {/* Spec sheet */}
          <div className="mt-6">
            <DataRow label="Molecular Formula" value={product.molecular_formula} />
            <DataRow label="CAS Number" value={product.cas_number} />
            <DataRow label="Batch / Lot No." value={product.batch_number} />
            <DataRow label="Sequence" value={product.sequence} />
            <DataRow label="Stock" value={product.stock > 0 ? `${product.stock} units` : "Out of stock"} />
          </div>

          {/* COA */}
          {product.coa_file_path ? (
            <a
              href={`${API}/files/${product.coa_file_path}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="coa-download-link"
              className="mt-6 flex items-center gap-3 border-l-2 border-cyan bg-navy-surface px-4 py-3 font-mono text-sm text-white transition-colors hover:bg-navy-elevated"
            >
              <FileArrowDown size={20} className="text-cyan" />
              <span className="uppercase tracking-wider">
                Download Certificate of Analysis
              </span>
              <span className="ml-auto text-[11px] text-teal">{product.coa_filename || "PDF"}</span>
            </a>
          ) : (
            <div className="mt-6 flex items-center gap-3 border-l-2 border-teal/30 bg-navy-surface px-4 py-3 font-mono text-xs uppercase tracking-wider text-teal-muted">
              <FileArrowDown size={18} /> COA available on request
            </div>
          )}

          {/* Price + cart */}
          <div className="mt-8 flex flex-wrap items-center gap-4 border-t border-cyan/15 pt-8">
            <span className="font-mono text-3xl font-bold text-white" data-testid="product-price">
              {formatCurrency(product.price)}
            </span>
            <div className="flex items-center border border-cyan/20">
              <button
                data-testid="qty-decrease"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="flex h-11 w-11 items-center justify-center text-teal-muted transition-colors hover:text-cyan"
              >
                <Minus size={16} />
              </button>
              <span data-testid="qty-value" className="w-12 text-center font-mono text-white">{qty}</span>
              <button
                data-testid="qty-increase"
                onClick={() => setQty((q) => q + 1)}
                className="flex h-11 w-11 items-center justify-center text-teal-muted transition-colors hover:text-cyan"
              >
                <Plus size={16} />
              </button>
            </div>
            <button
              data-testid="add-to-cart-detail"
              onClick={addToCart}
              className="flex flex-1 items-center justify-center gap-2 bg-cyan px-6 py-3.5 font-mono text-sm font-semibold uppercase tracking-wider text-navy-deep transition-all duration-300 hover:bg-cyan-bright hover:shadow-glow"
            >
              <ShoppingCartSimple size={18} weight="bold" /> Add to Cart
            </button>
          </div>

          {/* Disclaimer */}
          <div className="mt-6 flex items-start gap-3 border border-cyan/10 bg-navy-deep p-4">
            <Warning size={18} weight="fill" className="mt-0.5 shrink-0 text-cyan" />
            <p className="font-mono text-[11px] leading-relaxed uppercase tracking-wider text-teal-muted">
              This product is intended strictly for laboratory and research use only. Not for human
              or veterinary consumption, diagnostic, or therapeutic use.
            </p>
          </div>

          <div className="mt-3 flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-teal">
            <ShieldCheck size={14} className="text-cyan" /> Third-party tested · Batch traceable
          </div>
        </motion.div>
      </div>
    </div>
  );
}
